import React, { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import { UserPlusIcon, CancelIcon, ChevronLeftIcon } from "./icons";

const AddClientModal = ({ isOpen, onClose, onAddClient, preselectedUser = null }) => {
  const [step, setStep] = useState(1); // 1: Search users, 2: Configure preferences
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [clientData, setClientData] = useState({
    userId: "",
    clientNotes: "",
    preferredGender: "any",
    preferredAgeRange: "",
    preferredCountries: [],
    preferredCities: [],
    preferredProfessions: [],
    preferredCastes: [],
    preferredHeightMinFeet: "",
    preferredHeightMinInches: "",
    preferredHeightMaxFeet: "",
    preferredHeightMaxInches: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  // Reset modal state when opened/closed. If a preselectedUser was provided
  // (e.g. "Add as Client" from a profile card), skip step 1 and jump straight
  // to the preferences step with that user already chosen.
  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setSearchResults([]);
      setError(null);

      if (preselectedUser && preselectedUser._id) {
        setSelectedUser(preselectedUser);
        setClientData({
          userId: preselectedUser._id,
          clientNotes: "",
          preferredGender: "any",
          preferredAgeRange: "",
          preferredCities: [],
          preferredProfessions: [],
          preferredCastes: [],
          preferredHeightMinFeet: "",
          preferredHeightMinInches: "",
          preferredHeightMaxFeet: "",
          preferredHeightMaxInches: "",
        });
        setStep(2);
      } else {
        setSelectedUser(null);
        setClientData({
          userId: "",
          clientNotes: "",
          preferredGender: "any",
          preferredAgeRange: "",
          preferredCities: [],
          preferredProfessions: [],
          preferredCastes: [],
          preferredHeightMinFeet: "",
          preferredHeightMinInches: "",
          preferredHeightMaxFeet: "",
          preferredHeightMaxInches: "",
        });
        setStep(1);
      }
    }
  }, [isOpen, preselectedUser]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ search: searchTerm });
      const data = await apiFetch(`/clients/search-users?${params}`);
      setSearchResults(data.data || []);
    } catch (err) {
      console.error("Search failed:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setClientData(prev => ({ ...prev, userId: user._id }));
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      setError("Please select a user first");
      return;
    }

    try {
      setGlobalLoadingMessage("Adding client...");
      setGlobalLoading(true);

      // Parse "25-35" -> { min: 25, max: 35 }. Empty / malformed -> omit.
      const parseAgeRange = (value) => {
        if (!value || typeof value !== "string") return undefined;
        const [minStr, maxStr] = value.split("-").map((s) => s.trim());
        const min = Number(minStr);
        const max = Number(maxStr);
        if (Number.isNaN(min) || Number.isNaN(max)) return undefined;
        return { min, max };
      };

      // Convert ft/in pair → total inches; null if both blank so we omit it.
      const toTotalInches = (ft, inch) => {
        const f = ft === "" || ft === null || ft === undefined ? null : Number(ft);
        const i = inch === "" || inch === null || inch === undefined ? null : Number(inch);
        const fOk = f !== null && !Number.isNaN(f);
        const iOk = i !== null && !Number.isNaN(i);
        if (!fOk && !iOk) return undefined;
        return (fOk ? f : 0) * 12 + (iOk ? i : 0);
      };

      const {
        preferredHeightMinFeet,
        preferredHeightMinInches,
        preferredHeightMaxFeet,
        preferredHeightMaxInches,
        ...rest
      } = clientData;

      const payload = {
        ...rest,
        preferredAgeRange: parseAgeRange(clientData.preferredAgeRange),
        preferredHeightMin: toTotalInches(
          preferredHeightMinFeet,
          preferredHeightMinInches
        ),
        preferredHeightMax: toTotalInches(
          preferredHeightMaxFeet,
          preferredHeightMaxInches
        ),
      };
      if (payload.preferredAgeRange === undefined) delete payload.preferredAgeRange;
      if (payload.preferredHeightMin === undefined) delete payload.preferredHeightMin;
      if (payload.preferredHeightMax === undefined) delete payload.preferredHeightMax;

      await onAddClient(payload);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const handleInputChange = (field, value) => {
    setClientData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayInputChange = (field, value) => {
    const array = value.split(',').map(item => item.trim()).filter(item => item);
    setClientData(prev => ({ ...prev, [field]: array }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--color-surface)] rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* HEADER — always visible */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--color-border)] flex-shrink-0">
          <h2 className="text-xl font-semibold text-[var(--color-text-strong)]">
            Add New Client
          </h2>
          <button
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          {/* BODY — scrolls */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Step Indicator */}
          <div className="flex items-center mb-6">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)]' : 'bg-[var(--color-border)] text-[var(--color-text-muted)]'}`}>
              1
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'}`}></div>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)]' : 'bg-[var(--color-border)] text-[var(--color-text-muted)]'}`}>
              2
            </div>
          </div>

          {/* Step 1: Search Users */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-[var(--color-text-strong)] mb-4">
                Search and Select User
              </h3>

              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  placeholder="Search by name, reference ID, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      // Stop the outer <form> from submitting on Enter.
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-50 transition-colors"
                >
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>

              {error && (
                <div className="text-red-500 text-sm mb-4">{error}</div>
              )}

              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {searchResults.map((user) => (
                    <div
                      key={user._id}
                      onClick={() => handleUserSelect(user)}
                      className="p-3 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-hover)] cursor-pointer transition-colors"
                    >
                      <div className="font-medium text-[var(--color-text-strong)]">
                        {user.fullName}
                      </div>
                      <div className="text-sm text-[var(--color-text-muted)]">
                        ID: {user.referenceId} • Age: {user.age} • {user.city}
                      </div>
                      <div className="text-sm text-[var(--color-text-muted)]">
                        {user.profession} • {user.contactNumber}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {searchResults.length === 0 && searchTerm && !loading && (
                <div className="text-center py-8 text-[var(--color-text-muted)]">
                  No users found. Try different search terms.
                </div>
              )}
            </div>
          )}

          {/* Step 2: Configure Preferences */}
          {step === 2 && selectedUser && (
            <div>
              <h3 className="text-lg font-medium text-[var(--color-text-strong)] mb-4">
                Configure Client Preferences
              </h3>

              {/* Selected User Info */}
              <div className="bg-[var(--color-surface-muted)] p-3 rounded-lg mb-4">
                <div className="font-medium text-[var(--color-text-strong)]">
                  {selectedUser.fullName}
                </div>
                <div className="text-sm text-[var(--color-text-muted)]">
                  ID: {selectedUser.referenceId} • Age: {selectedUser.age} • {selectedUser.city}
                </div>
              </div>

              {error && (
                <div className="text-red-500 text-sm mb-4">{error}</div>
              )}

              <div className="space-y-4">
                {/* Client Notes */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Client Notes (Optional)
                  </label>
                  <textarea
                    value={clientData.clientNotes}
                    onChange={(e) => handleInputChange('clientNotes', e.target.value)}
                    placeholder="Any special notes about this client..."
                    rows={3}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                </div>

                {/* Preferred Gender */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Preferred Gender
                  </label>
                  <select
                    value={clientData.preferredGender}
                    onChange={(e) => handleInputChange('preferredGender', e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  >
                    <option value="any">Any</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                {/* Preferred Age Range */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Preferred Age Range (Optional)
                  </label>
                  <input
                    type="text"
                    value={clientData.preferredAgeRange}
                    onChange={(e) => handleInputChange('preferredAgeRange', e.target.value)}
                    placeholder="e.g., 25-35"
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                </div>

                {/* Preferred Countries */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Preferred Countries (Optional)
                  </label>
                  <input
                    type="text"
                    value={clientData.preferredCountries.join(', ')}
                    onChange={(e) => handleArrayInputChange('preferredCountries', e.target.value)}
                    placeholder="e.g., Pakistan, UK, USA"
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                  <div className="text-xs text-[var(--color-text-muted)] mt-1">
                    Separate multiple countries with commas
                  </div>
                </div>

                {/* Preferred Cities */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Preferred Cities (Optional)
                  </label>
                  <input
                    type="text"
                    value={clientData.preferredCities.join(', ')}
                    onChange={(e) => handleArrayInputChange('preferredCities', e.target.value)}
                    placeholder="e.g., Lahore, Karachi, Islamabad"
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                  <div className="text-xs text-[var(--color-text-muted)] mt-1">
                    Separate multiple cities with commas
                  </div>
                </div>

                {/* Preferred Professions */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Preferred Professions (Optional)
                  </label>
                  <input
                    type="text"
                    value={clientData.preferredProfessions.join(', ')}
                    onChange={(e) => handleArrayInputChange('preferredProfessions', e.target.value)}
                    placeholder="e.g., Doctor, Engineer, Teacher"
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                  <div className="text-xs text-[var(--color-text-muted)] mt-1">
                    Separate multiple professions with commas
                  </div>
                </div>

                {/* Preferred Castes */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Preferred Castes (Optional)
                  </label>
                  <input
                    type="text"
                    value={clientData.preferredCastes.join(', ')}
                    onChange={(e) => handleArrayInputChange('preferredCastes', e.target.value)}
                    placeholder="e.g., Rajput, Syed, Mughal"
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                  <div className="text-xs text-[var(--color-text-muted)] mt-1">
                    Separate multiple castes with commas
                  </div>
                </div>

                {/* Preferred Height Range */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Preferred Height Range (Optional)
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-[var(--color-text-muted)] mb-1">Min</div>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="0"
                          max="8"
                          placeholder="ft"
                          value={clientData.preferredHeightMinFeet}
                          onChange={(e) => handleInputChange('preferredHeightMinFeet', e.target.value)}
                          className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                        />
                        <input
                          type="number"
                          min="0"
                          max="11"
                          placeholder="in"
                          value={clientData.preferredHeightMinInches}
                          onChange={(e) => handleInputChange('preferredHeightMinInches', e.target.value)}
                          className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-[var(--color-text-muted)] mb-1">Max</div>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="0"
                          max="8"
                          placeholder="ft"
                          value={clientData.preferredHeightMaxFeet}
                          onChange={(e) => handleInputChange('preferredHeightMaxFeet', e.target.value)}
                          className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                        />
                        <input
                          type="number"
                          min="0"
                          max="11"
                          placeholder="in"
                          value={clientData.preferredHeightMaxInches}
                          onChange={(e) => handleInputChange('preferredHeightMaxInches', e.target.value)}
                          className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}
          </div>

          {/* FOOTER — always visible. Buttons swap by step. */}
          <div className="flex justify-between gap-3 px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">
            <button
              type="button"
              onClick={
                step === 2 && !preselectedUser ? () => setStep(1) : onClose
              }
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] px-4 py-2 text-sm font-semibold transition-colors"
            >
              {step === 2 && !preselectedUser ? (
                <ChevronLeftIcon className="w-4 h-4" />
              ) : (
                <CancelIcon className="w-4 h-4" />
              )}
              {step === 2 && !preselectedUser ? "Back" : "Cancel"}
            </button>
            <button
              type="submit"
              disabled={step !== 2}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-50 px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
            >
              <UserPlusIcon className="w-4 h-4" />
              Add Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClientModal;